import { lazy } from "react"
import { Navigate, createBrowserRouter } from "react-router-dom"
import Loadable from "./Loadable"
import WrapperLayout from "./layouts/WrapperLayout"
import PerformanceReportLayout from "./layouts/PerformanceReportLayout"
import { accessControlFunc } from "src/components/accessControl"
import {
  selectUserAccess,
  selectUserAccessLoading
} from "src/redux/slicers/homeSlice"
import { useSelector } from "react-redux"

const Locations = Loadable(lazy(() => import("src/pages/buildings")))
const BuildingSummaryPage = Loadable(
  lazy(() => import("src/pages/building/summary"))
)
const Building = Loadable(lazy(() => import("src/pages/building")))
const BuildingPerformance = Loadable(
  lazy(() => import("src/pages/building/performance"))
)
const BuildingServiceAdvisoriesPage = Loadable(
  lazy(() => import("src/pages/building/service-advisories"))
)

/* Equipment Pages */
const Equipment = Loadable(lazy(() => import("src/pages/building/equipment")))
const EquipmentSummaryPage = Loadable(
  lazy(() => import("src/pages/building/equipment/summary"))
)
const EquipmentPerformancePage = Loadable(
  lazy(() => import("src/pages/building/equipment/performance"))
)
const EquipmentDIReportPage = Loadable(
  lazy(() => import("src/pages/building/equipment/di-report"))
)
const EquipmentServiceAdvisoriesPage = lazy(
  () => import("src/pages/building/equipment/service-advisories")
)
const VRFDigitalInspectionPage = lazy(
  () => import("src/pages/building/equipment/vrf-digital-inspection")
)
const EquipmentDigitalInspectionPageV2 = lazy(
  () => import("src/pages/building/equipment/digital-inspection-v2")
)
const DigitalInspectionReportV2 = lazy(
  () =>
    import(
      "src/pages/building/DigitalInspectionReportV2/Digital-inspection-Report"
    )
)

const DigitalInspectionAIReport = lazy(
  () => import("src/pages/building/equipment/AIReport")
)
/* Admin Pages */
// Analytics pages
const AdminSettingsActionBar = Loadable(
  lazy(() => import("src/pages/admin-settings"))
)

const AnalyticsActionBar = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics"))
)

const AnalyticsSetupPage = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/setup"))
)
const AnalyticsSubscriptionPage = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/subscription"))
)
const AnalyticsReprocessingPage = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/reprocessing"))
)
const AnalyticsExecuterPage = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/executer"))
)
const AnalyticsExecuterV2Page = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/executer-v2"))
)
const AnalyticsDebuggerPage = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/debugger"))
)
const AddUpdateAnalytics = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/add-update-analytic"))
)
const TimeStreamAnalytics = Loadable(
  lazy(() => import("src/pages/admin-settings/analytics/time-stream-analytics"))
)
// Entities Page and sub pages
const EntitiesActionBar = Loadable(
  lazy(() => import("src/pages/admin-settings/entities"))
)
const EntitiesPage = Loadable(
  lazy(() => import("src/pages/admin-settings/entities/entities-common-page"))
)

// Permissions Page and sub pages
const PermissionActionBar = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions"))
)
const PermissionsBuildingPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/buildings"))
)
const PermissionsApplicationsPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/applications"))
)
const PermissionsUserTypesPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/user-types"))
)
const PermissionsUserPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/users"))
)
const PermissionGroupPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/groups"))
)
const UiPermissionsPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/ui-permissions"))
)
const PermissionGroupPermissionPage = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/group-permissions"))
)
const PermissionModal = Loadable(
  lazy(() => import("src/pages/admin-settings/permissions/permissions-modal"))
)

// Subscriptions Page and sub pages
const SubscriptionsActionBar = Loadable(
  lazy(() => import("src/pages/admin-settings/subscriptions"))
)

// fault detection poc page
const AnomalyBuildingsView = Loadable(
  lazy(
    () =>
      import("src/pages/fault-detection/AnomalyViews/anomaly-buildings-view")
  )
)
const AnomalyEquipmentsView = Loadable(
  lazy(
    () =>
      import("src/pages/fault-detection/AnomalyViews/anomaly-equipments-view")
  )
)
const AnomalyTwinView = Loadable(
  lazy(() => import("src/pages/fault-detection/AnomalyViews/twin-view"))
)
const AnomalyMapsView = Loadable(
  lazy(() => import("src/pages/fault-detection/AnomalyViews/map-view"))
)
const RuleWizard = Loadable(
  lazy(() => import("src/pages/fault-detection/rules-wizard"))
)

const SubscriptionOfferingsPage = Loadable(
  lazy(() => import("src/pages/admin-settings/subscriptions/offerings"))
)
const BuildingsSubscriptionPage = Loadable(
  lazy(
    () =>
      import("src/pages/admin-settings/subscriptions/building-subscriptions")
  )
)

const NotFound = Loadable(lazy(() => import("src/pages/notfound")))
const FindingsList = Loadable(lazy(() => import("src/pages/building/findings")))
const FindingDetails = Loadable(
  lazy(() => import("src/pages/building/findings/findings-detail-view"))
)
const ProtectedRoute = Loadable(lazy(() => import("src/pages/protectedRoute")))
const Reports = Loadable(lazy(() => import("src/pages/building/reports")))
const EquipmentReports = Loadable(
  lazy(() => import("src/pages/building/equipment/reports"))
)

/**
 * Wrapper component for ProtetedRoute to add aditional props that is required
 *
 * @param props
 * @returns
 */
function ProtectedRouteWithUserAccessDetails(props) {
  const userAccess = useSelector(selectUserAccess)

  const accessLoading = useSelector(selectUserAccessLoading)

  const permissionId = props.permissionId || "cc.pages.adminSettings"

  const access = accessControlFunc({ id: permissionId, userAccess })

  return (
    <ProtectedRoute access={access} accessLoading={accessLoading} {...props} />
  )
}

function ProtectedRoutesForPOC(props) {
  const userAccess = useSelector(selectUserAccess)

  const accessLoading = useSelector(selectUserAccessLoading)

  const access = accessControlFunc({ id: "cc.poc", userAccess })

  return (
    <ProtectedRoute access={access} accessLoading={accessLoading} {...props} />
  )
}

// Application route objects
const routes = [
  {
    path: "report",
    element: <PerformanceReportLayout />,
    children: [
      {
        path: "building/:buildingId/performance",
        element: <BuildingPerformance />
      },
      //building/95d50391-348e-4e96-8c6f-f34123085c2b/equipment/7f2b7e66-3716-4922-b6d4-a856cc3a6ee7/digital-inspection-report-v2
      {
        path: "building/:buildingId/equipment/:equipmentId/performance",
        element: <EquipmentPerformancePage />
      },
      {
        path: "building/:buildingId/equipment/:equipmentId/digital-inspection-report-v2",
        element: <DigitalInspectionReportV2 />
      },
      {
        path: "building/:buildingId/digital-inspection-report-v2",
        element: <DigitalInspectionReportV2 />
      }
    ]
  },
  {
    path: "DIreport",
    element: <PerformanceReportLayout />,
    children: [
      {
        path: "building/:buildingId/equipment/:equipmentId",
        element: <EquipmentDIReportPage />
      }
    ]
  },
  {
    path: "/",
    element: <WrapperLayout />,
    children: [
      {
        path: "/",
        element: <Navigate to="/buildings?view=tiles" replace />
      },
      {
        path: "buildings",
        element: <Locations />
      },
      {
        path: "rules-wizard",
        element: <ProtectedRoutesForPOC Route={RuleWizard} />
      },
      {
        path: "anomaly-buildings-view",
        element: <ProtectedRoutesForPOC Route={AnomalyBuildingsView} />
      },
      {
        path: "anomaly-equipments-view",
        element: <ProtectedRoutesForPOC Route={AnomalyEquipmentsView} />
      },
      {
        path: "anomaly-maps-view",
        element: <ProtectedRoutesForPOC Route={AnomalyMapsView} />
      },
      {
        path: "anomaly-twin-view",
        element: <ProtectedRoutesForPOC Route={AnomalyTwinView} />
      },
      {
        path: "building/:buildingId",
        element: <Building />,
        children: [
          {
            path: "",
            element: <Navigate to="summary" replace />
          },
          {
            path: "summary",
            element: <BuildingSummaryPage />
          },
          {
            path: "performance",
            element: <BuildingPerformance />
          },
          {
            path: "advisories",
            element: <BuildingServiceAdvisoriesPage />
          },
          {
            path: "reports",
            element: <Reports />
          },
          {
            path: "digital-inspection-report-v2",
            element: <DigitalInspectionReportV2 />
          },
          {
            path: "findings",
            element: <FindingsList />
          },
          {
            path: "finding",
            element: <FindingDetails />
          },
          {
            path: "finding/:findingId",
            element: <FindingDetails />
          }
        ]
      },
      {
        path: "building/:buildingId/equipment/:equipmentId",
        element: <Equipment />,
        children: [
          { path: "summary", element: <EquipmentSummaryPage /> },
          {
            path: "performance",
            element: <EquipmentPerformancePage />
          },
          {
            path: "findings",
            element: <FindingsList />
          },
          {
            path: "finding",
            element: <FindingDetails />
          },
          {
            path: "finding/:findingId",
            element: <FindingDetails />
          },
          {
            path: "advisories",
            element: <EquipmentServiceAdvisoriesPage />
          },
          {
            path: "vrf-digital-inspection",
            element: <VRFDigitalInspectionPage />
          },
          {
            path: "digital-inspection-v2",
            element: <EquipmentDigitalInspectionPageV2 />
          },
          {
            path: "digital-inspection-report-v2",
            element: <DigitalInspectionReportV2 />
          },
          {
            path: "digital-inspection-ai-report",
            element: (
              <ProtectedRoutesForPOC Route={DigitalInspectionAIReport} />
            )
          },
          {
            path: "reports",
            element: <EquipmentReports />
          }
        ]
      },
      {
        path: "/admin-settings",
        element: (
          <ProtectedRouteWithUserAccessDetails Route={AdminSettingsActionBar} />
        ),
        children: [
          {
            path: "permissions",
            element: (
              <ProtectedRouteWithUserAccessDetails
                Route={PermissionActionBar}
              />
            ),
            children: [
              {
                path: "buildings",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionsBuildingPage}
                    basePage="permissions"
                  />
                ),
                children: [
                  {
                    path: "modal",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={PermissionModal}
                      />
                    )
                  }
                ]
              },
              {
                path: "applications",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionsApplicationsPage}
                    basePage="permissions"
                  />
                ),
                children: [
                  {
                    path: "modal",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={PermissionModal}
                      />
                    )
                  }
                ]
              },
              {
                path: "user-types",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionsUserTypesPage}
                    basePage="permissions"
                  />
                ),
                children: [
                  {
                    path: "modal",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={PermissionModal}
                      />
                    )
                  }
                ]
              },
              {
                path: "group-permissions",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionsUserTypesPage}
                    basePage="permissions"
                  />
                )
              },
              {
                path: "users",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionsUserPage}
                  />
                ),
                children: [
                  {
                    path: "modal",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={PermissionModal}
                      />
                    )
                  }
                ]
              },
              {
                path: "groups",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionGroupPage}
                  />
                ),
                children: [
                  {
                    path: "modal",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={PermissionModal}
                      />
                    )
                  }
                ]
              },
              {
                path: "ui-permissions-demo",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={UiPermissionsPage}
                  />
                )
              }
            ]
          },
          {
            path: "analytics",
            element: (
              <ProtectedRouteWithUserAccessDetails Route={AnalyticsActionBar} />
            ),
            children: [
              {
                path: "setup",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={AnalyticsSetupPage}
                  />
                ),
                children: [
                  {
                    path: "add-analytic",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={AddUpdateAnalytics}
                      />
                    )
                  },
                  {
                    path: "update-analytic",
                    element: (
                      <ProtectedRouteWithUserAccessDetails
                        Route={AddUpdateAnalytics}
                      />
                    )
                  }
                ]
              },
              {
                path: "subscribe",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={AnalyticsSubscriptionPage}
                  />
                )
              },
              {
                path: "reprocessing",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={AnalyticsReprocessingPage}
                  />
                )
              },
              {
                path: "debugger",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={AnalyticsDebuggerPage}
                  />
                )
              },
              {
                path: "executer",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={AnalyticsExecuterPage}
                  />
                )
              },
              {
                path: "executer-v2",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={AnalyticsExecuterV2Page}
                  />
                )
              },
              {
                path: "time-stream-analytics",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={TimeStreamAnalytics}
                  />
                )
              }
            ]
          },
          {
            path: "subscriptions",
            element: <SubscriptionsActionBar />,
            children: [
              {
                path: "buildings",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={PermissionsBuildingPage}
                    basePage="subscriptions"
                  />
                )
              },
              {
                path: "offerings",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={SubscriptionOfferingsPage}
                  />
                )
              },
              {
                path: "building-subscriptions",
                element: (
                  <ProtectedRouteWithUserAccessDetails
                    Route={BuildingsSubscriptionPage}
                  />
                )
              }
            ]
          },
          {
            path: "entities",
            element: (
              <ProtectedRouteWithUserAccessDetails Route={EntitiesActionBar} />
            ),
            children: [
              {
                path: ":pageId",
                element: (
                  <ProtectedRouteWithUserAccessDetails Route={EntitiesPage} />
                )
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: "*",
    element: <NotFound />
  }
]

export const appRouter = createBrowserRouter(routes, {
  basename: "/"
})

